import axios from 'axios';
import cookie from 'js-cookie';
import { formatDate } from '../../../utils';

// Get the API base URL from environment variables or use a default
const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Fetch project details by project ID
 * @param {string} projectId - The ID of the project
 * @returns {Promise<Object>} - Project data
 */
export const fetchProjectById = async (projectId) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/project/get-project/${projectId}`, {
            withCredentials: true,
            headers: {
                'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
            },
        });

        const projectData = response.data;

        // Transform the data to match our frontend needs
        return {
            id: projectData.projectId,
            name: projectData.name,
            fieldId: projectData.fieldId,
            businessId: projectData.businessId,
            businessName: projectData.businessName || '',
            address: [projectData.streetAddress, projectData.city, projectData.province]
                .filter(Boolean)
                .join(', '),
            createdAt: projectData.creationDate,
            formattedCreatedAt: formatDate(projectData.creationDate),
            status: projectData.isActive ? 'Active' : 'Inactive',
            description: projectData.description,
            shareInternalDeficienciesExternally: projectData.shareInternalDeficienciesExternally,
            allowExternalSubmissions: projectData.allowExternalSubmissions,
            externalSubmissionDeadline: projectData.externalSubmissionDeadline,
            // server-derived open/closed decision (uses server UTC date) — prefer this over
            // comparing the deadline against the client clock.
            externalSubmissionsAllowed: projectData.externalSubmissionsAllowed,
            autoDueDateEnabled: projectData.autoDueDateEnabled,
            autoDueDateDays: projectData.autoDueDateDays,
            floorplanGroupingEnabled: projectData.floorplanGroupingEnabled,
            buildingNames: projectData.buildingNames || [],
            deficiencyCounts: {
                submittedCount: projectData.submittedCount || 0,
                acceptedCount: projectData.acceptedCount || 0,
                assignedCount: projectData.assignedCount || 0,
                pendingInternalSignoffCount: projectData.pendingFortisSignoffCount || 0,
                pendingExternalSignoffCount: projectData.pendingOACTSignoffCount || 0,
                completedCount: projectData.completedCount || 0,
            },
            differedValue: projectData.totalDeferredCost || 0,
        };
    } catch (error) {
        console.error('Error fetching project details:', error);
        throw error;
    }
};

/**
 * Update project details
 * @param {string} projectId - The ID of the project
 * @param {Object} projectData - The updated project data
 * @returns {Promise<Object>} - Updated project data
 */
export const updateProject = async (projectId, projectData) => {
    try {
        // Prepare the data for the API - include all required fields
        const apiData = {
            projectId: projectId,
            name: projectData.name,
            fieldId: projectData.fieldId,
            description: projectData.description,
            isActive: projectData.isActive,
            businessId: projectData.businessId,
            shareInternalDeficienciesExternally: projectData.shareInternalDeficienciesExternally,
            allowExternalSubmissions: projectData.allowExternalSubmissions,
            externalSubmissionDeadline: projectData.externalSubmissionDeadline,
            autoDueDateEnabled: projectData.autoDueDateEnabled,
            autoDueDateDays: projectData.autoDueDateDays,
        };

        // Send the update request
        const response = await axios.put(
            `${API_BASE_URL}/project/update-project/${projectId}`,
            apiData,
            {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            }
        );

        const updatedProjectData = response.data;

        // Transform the response data to match our frontend needs
        return {
            id: updatedProjectData.projectId,
            name: updatedProjectData.name,
            fieldId: updatedProjectData.fieldId,
            businessId: updatedProjectData.businessId,
            address: [
                updatedProjectData.streetAddress,
                updatedProjectData.city,
                updatedProjectData.province,
            ]
                .filter(Boolean)
                .join(', '),
            createdAt: updatedProjectData.creationDate,
            formattedCreatedAt: formatDate(updatedProjectData.creationDate),
            status: updatedProjectData.isActive ? 'Active' : 'Inactive',
            description: updatedProjectData.description,
            shareInternalDeficienciesExternally:
                updatedProjectData.shareInternalDeficienciesExternally,
            allowExternalSubmissions: updatedProjectData.allowExternalSubmissions,
            externalSubmissionDeadline: updatedProjectData.externalSubmissionDeadline,
            autoDueDateEnabled: updatedProjectData.autoDueDateEnabled,
            autoDueDateDays: updatedProjectData.autoDueDateDays,
            floorplanGroupingEnabled: updatedProjectData.floorplanGroupingEnabled,
            buildingNames: updatedProjectData.buildingNames || [],
            deficiencyCounts: projectData.deficiencyCounts,
            differedValue: updatedProjectData.totalDeferredCost || 0,
        };
    } catch (error) {
        console.error('Error updating project:', error);
        throw error;
    }
};

/**
 * Get all users for business
 * @returns {Promise<Array>} - Array of users with business information
 */
export const getAllUsersForBusiness = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/user/get-users-with-business`, {
            withCredentials: true,
            headers: {
                'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
            },
        });

        return response.data;
    } catch (error) {
        console.error('Error fetching users for business:', error);
        throw error;
    }
};

/**
 * Add multiple project members in one request.
 * @param {string} projectId - The project ID receiving the members
 * @param {Array<Object>} membershipsData - Project membership payloads
 * @returns {Promise<Array>} - Created memberships
 */
export const addProjectMembers = async (projectId, membershipsData) => {
    try {
        const response = await axios.post(
            `${API_BASE_URL}/project-membership/create-project-memberships/${projectId}`,
            membershipsData,
            {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            }
        );

        return response.data;
    } catch (error) {
        console.error('Error adding project members:', error);
        throw error;
    }
};

/**
 * Remove a project member
 * @param {string} membershipId - The ID of the membership to remove
 * @returns {Promise<Object>} - Response data
 */
export const removeProjectMember = async (membershipId) => {
    try {
        const response = await axios.delete(
            `${API_BASE_URL}/project-membership/delete-project-membership/${membershipId}`,
            {
                withCredentials: true,
                headers: {
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            }
        );

        return response.data;
    } catch (error) {
        console.error('Error removing project member:', error);
        throw error;
    }
};

/**
 * List project members by project ID
 * @param {string} projectId - The ID of the project
 * @returns {Promise<Array>} - Array of project members
 */
export const listProjectMembers = async (projectId) => {
    try {
        const response = await axios.get(
            `${API_BASE_URL}/project-membership/get-users-by-project-id/${projectId}`,
            {
                withCredentials: true,
                headers: {
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            }
        );

        return response.data;
    } catch (error) {
        console.error('Error fetching project members:', error);
        throw error;
    }
};
